// Campaigns ในหน้า config (ใช้ร่วมกันทั้งสินค้าและกลุ่ม) — สองชั้น:
//   ชั้น 1 รายการแคมเปญ · ชั้น 2 สิ่งที่ผูกกับแคมเปญนั้น (KOL / Ads / …)
//
// ประเภทที่ผูกได้อยู่ใน LINK_TYPES — เพิ่มประเภทใหม่ในอนาคตแค่เติมรายการนั้น
// (ready:false = ยังไม่เปิดใช้ แสดงเป็นหมวดเทา ๆ ไว้ให้เห็นโครง)
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Campaign, CampaignLink } from "@contracts/api";
import { campaignKeys } from "@features/campaigns/api/campaigns-api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { IconPencil, IconSearch } from "@shared/ui/icons";
import { SearchSelect } from "@shared/ui/search-select";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { fmtBaht } from "@shared/lib/format";
import { cn } from "@shared/lib/utils";
import { apiErrorMessage } from "@shared/api/client";
import {
  addCampaignLink,
  createCampaign,
  deleteCampaign,
  fetchProjectCodes,
  projectCodeKeys,
  renameCampaign,
  setCampaignMonthly,
  deleteCampaignLink,
  fetchKolCampaign,
} from "../api/settings-api";
import { useBiddableProjects, useKolCampaign, useKolCampaigns } from "../api/use-settings";

type LinkType = {
  kind: string;
  label: string;
  icon: string;
  ready: boolean;
  addLabel?: string;
  manualPlaceholder?: string;
  empty: string;
  /** "project" = ผูกกับงานฝั่ง Biddable (เลือกจากรายชื่อ project ของเขา) */
  platform?: "project";
};

const LINK_TYPES: LinkType[] = [
  {
    kind: "kol",
    label: "KOL Report",
    icon: "🎯",
    ready: true,
    addLabel: "เพิ่ม KOL Report",
    manualPlaceholder: "กรอกรหัสแคมเปญ KOL เช่น 00001",
    empty: "ยังไม่ได้ผูก KOL Report — เมนู Ads › KOL จะยังไม่มีข้อมูลแสดง",
  },
  /**
   * ผูกกับ "project" ฝั่ง Biddable (รหัสงานในชื่อแคมเปญ เช่น P2026-124)
   *
   * เดิมแยกเป็น Facebook Ads / TikTok Ads แล้วผูกทีละ Ad Account + ชื่อแคมเปญ
   * ซึ่งชี้ไปหน้า Creative Report ที่ฝั่งเขาลบทิ้งไปแล้ว · หน้าใหม่ (Campaign Report)
   * จัดกลุ่มเป็น project ซึ่งรวมทุก platform ของงานนั้นไว้ด้วยกัน จึงผูกจุดเดียวพอ
   * ลิงก์ ads/ads_tiktok เก่ายังอยู่ในฐานข้อมูล แต่ไม่แสดงแล้วเพราะปลายทางไม่มีอยู่
   */
  {
    kind: "ads_project",
    label: "Online Ads",
    icon: "📣",
    ready: true,
    platform: "project",
    addLabel: "เพิ่ม project",
    manualPlaceholder: "กรอกรหัส project เช่น P2026-124",
    empty: "ยังไม่ได้ผูก project — เมนู Ads › Online Ads จะยังไม่มีข้อมูลแสดง",
  },
  {
    kind: "quotation",
    label: "Quotation (PCE)",
    icon: "🧾",
    ready: false,
    empty: "เร็วๆ นี้ — จะดึงใบเสนอราคาจากระบบ PCE",
  },
  {
    kind: "media_plan",
    label: "Media Plan",
    icon: "🗂️",
    ready: false,
    empty: "เร็วๆ นี้ — จะเชื่อม Media Plan",
  },
];

const linksOf = (c: Campaign, kind: string) => (c.links ?? []).filter((l) => l.kind === kind);

/** สิ่งที่ต้องรู้เพื่อผูกหนึ่งรายการ — kind มาจากหมวดที่กดเพิ่ม ไม่ต้องส่งมาด้วย */
type LinkInput = { refId: string; refName?: string | null; meta?: unknown };

export function CampaignsSection({ brandId }: { brandId: string }) {
  const { data, isPending } = useCampaigns(brandId);
  const list = data ?? [];
  const [detailId, setDetailId] = useState<string | null>(null);
  const detail = list.find((c) => c.id === detailId) ?? null;

  if (isPending) return <p className="muted small-note">กำลังโหลดแคมเปญ…</p>;
  if (detail)
    return <CampaignDetail campaign={detail} brandId={brandId} onBack={() => setDetailId(null)} />;
  return <CampaignList brandId={brandId} list={list} onOpen={setDetailId} />;
}

/* ───────── ชั้นที่ 1: รายการแคมเปญ ───────── */

function CampaignList({
  brandId,
  list,
  onOpen,
}: {
  brandId: string;
  list: Campaign[];
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: campaignKeys.byProduct(brandId) });

  const [finding, setFinding] = useState(false);

  /**
   * ทะเบียนงานของแบรนด์นี้ — ใช้สองที่: ผลค้นหา และป้ายสถานะบนแถวแคมเปญที่สร้างแล้ว
   *
   * สถานะเปลี่ยนได้เรื่อย ๆ ในชีต (proposal → active → complete) จึงอ่านสด ๆ ทุกครั้ง
   * ไม่เก็บลงฐานข้อมูลตอนสร้างแคมเปญ ไม่งั้นป้ายจะค้างอยู่ที่สถานะวันที่กดสร้าง
   */
  const register = useQuery({
    queryKey: projectCodeKeys.byBrand(brandId),
    queryFn: () => fetchProjectCodes(brandId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  /** รหัส P2026-020 → สถานะ · คีย์เป็นตัวพิมพ์ใหญ่เพื่อเทียบกับชื่อแคมเปญได้ตรง */
  const statusByCode = new Map(
    (register.data?.projects ?? [])
      .filter((p) => p.code)
      .map((p) => [p.code.toUpperCase(), p.status]),
  );

  const add = useMutation({
    mutationFn: (only?: string) =>
      createCampaign({ productCode: brandId, name: (only ?? name).trim() }),
    onSuccess: () => {
      setName("");
      setMsg("");
      void invalidate();
    },
    onError: (e: unknown) => {
      const body = (e as { response?: { data?: { error?: string } } })?.response?.data;
      setMsg(body?.error || "สร้างแคมเปญไม่สำเร็จ");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCampaign(id),
    onSuccess: invalidate,
  });

  /** ชื่อแคมเปญไปโผล่ใน dropdown ของหน้า My Job — พิมพ์ผิดแล้วต้องแก้ได้ ไม่ใช่ลบแล้วสร้างใหม่
   *  (ลบแล้วสร้างใหม่ = สิ่งที่ผูกไว้ทั้งหมดหายไปด้วย) */
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) => renameCampaign(v.id, v.name),
    onSuccess: () => {
      setEditId(null);
      setMsg("");
      void invalidate();
    },
    onError: (e: unknown) => {
      const body = (e as { response?: { data?: { error?: string } } })?.response?.data;
      setMsg(body?.error || "เปลี่ยนชื่อไม่สำเร็จ");
    },
  });

  const startEdit = (c: Campaign) => {
    setMsg("");
    setEditId(c.id);
    setDraft(c.name);
  };

  /** ว่างหรือไม่ได้เปลี่ยน = ปิดโหมดแก้เฉย ๆ ไม่ต้องยิงคำขอ */
  const commitEdit = (c: Campaign) => {
    const next = draft.trim();
    if (!next || next === c.name) {
      setEditId(null);
      return;
    }
    rename.mutate({ id: c.id, name: next });
  };

  const doAdd = () => {
    setMsg("");
    if (!name.trim()) {
      setMsg("กรุณาใส่ชื่อแคมเปญ");
      return;
    }
    add.mutate(undefined);
  };

  return (
    <>
      <div className="camp-create">
        <div className="camp-create-main">
          <input
            type="text"
            placeholder="ชื่อแคมเปญใหม่…"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doAdd()}
          />
          <button type="button" className="btn-primary" disabled={add.isPending} onClick={doAdd}>
            + สร้างแคมเปญ
          </button>
          {/* ชื่องานมีอยู่ในทะเบียน Project Code แล้ว — หยิบมาดีกว่าพิมพ์ใหม่ให้ต่างกัน */}
          <button
            type="button"
            className={cn("btn-outline", finding && "is-on")}
            onClick={() => setFinding((v) => !v)}
          >
            <IconSearch size={15} /> ค้นหา campaign
          </button>
        </div>
        <p className="muted small-note">{msg}</p>
      </div>

      {finding && (
        <ProjectFinder
          brandId={brandId}
          taken={new Set(list.map((c) => c.name.trim().toLowerCase()))}
          onCreate={(name) => add.mutate(name)}
          busy={add.isPending}
        />
      )}

      <div className="camp-list">
        {!list.length ? (
          <p className="muted small-note">
            ยังไม่มีแคมเปญ — สร้างด้านบน แล้วชื่อจะไปแสดงใน dropdown ของหน้า My Job (การแสดงผล Ads
            จะขึ้นกับแคมเปญที่เลือก)
          </p>
        ) : (
          list.map((c) => (
            <div className="camp-row" key={c.id}>
              <span className="camp-ic">🎯</span>
              <span className="grow">
                {editId === c.id ? (
                  <input
                    type="text"
                    className="camp-name-edit"
                    maxLength={120}
                    autoFocus
                    value={draft}
                    disabled={rename.isPending}
                    onChange={(e) => setDraft(e.target.value)}
                    onFocus={(e) => e.currentTarget.select()}
                    onBlur={() => commitEdit(c)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitEdit(c);
                      // Esc ต้องยกเลิกโดยไม่บันทึก — ปิดก่อนแล้ว blur จะไม่เจอ editId
                      // ของแถวนี้อีก จึงไม่ยิงคำขอตามมา
                      else if (e.key === "Escape") setEditId(null);
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="camp-name camp-name-btn"
                    title="กดเพื่อเปลี่ยนชื่อ"
                    onClick={() => startEdit(c)}
                  >
                    {c.name}
                    <IconPencil size={13} />
                  </button>
                )}
                <span className="camp-chips">
                  {(() => {
                    // ชื่อแคมเปญที่สร้างจากทะเบียนขึ้นต้นด้วย [P2026-020] — ดึงรหัสจากตรงนั้น
                    const code = /^\[(P\d{4}-\d+)\]/i.exec(c.name.trim())?.[1]?.toUpperCase();
                    const st = code ? statusByCode.get(code) : undefined;
                    return st ? (
                      <span className={cn("pf-status", statusTone(st))}>{st}</span>
                    ) : null;
                  })()}
                  {LINK_TYPES.map((t) => {
                    const n = linksOf(c, t.kind).length;
                    return (
                      <span key={t.kind} className={cn("link-chip", n && "has")} title={t.label}>
                        {t.icon} {t.label} <b>{n}</b>
                      </span>
                    );
                  })}
                </span>
              </span>
              <button
                type="button"
                className="btn-outline btn-sm camp-open"
                onClick={() => onOpen(c.id)}
              >
                จัดการ →
              </button>
              <button
                type="button"
                className="icon-btn camp-del"
                title="ลบแคมเปญ"
                disabled={remove.isPending}
                onClick={() => {
                  if (!confirm("ลบแคมเปญนี้? สิ่งที่ผูกไว้กับแคมเปญจะถูกเอาออกด้วย")) return;
                  remove.mutate(c.id);
                }}
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/**
 * งานในทะเบียน Project Code ของแบรนด์นี้ — กดสร้างเป็นแคมเปญได้ทีละอัน
 *
 * server เป็นคนจับคู่ชื่อแบรนด์ให้ (ชื่อในสองระบบไม่ตรงกันเป๊ะ) หน้านี้แค่แสดงผลกับ
 * กรองต่ออีกชั้นด้วยคำที่พิมพ์ เพราะบางแบรนด์มีงานเป็นหกสิบรายการย้อนหลังหลายปี
 */
function ProjectFinder({
  brandId,
  taken,
  onCreate,
  busy,
}: {
  brandId: string;
  /** ชื่อแคมเปญที่มีอยู่แล้ว (ตัวพิมพ์เล็ก) — กันสร้างซ้ำ */
  taken: Set<string>;
  onCreate: (name: string) => void;
  busy: boolean;
}) {
  const [q, setQ] = useState("");
  /** จำนวนปีที่กางอยู่ — เริ่มที่ปีล่าสุดปีเดียว */
  const [yearsOpen, setYearsOpen] = useState(1);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: projectCodeKeys.byBrand(brandId),
    queryFn: () => fetchProjectCodes(brandId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isPending) return <p className="muted small-note pf-note">กำลังอ่านทะเบียนงานจาก Sheet…</p>;

  if (isError)
    return (
      <p className="muted small-note pf-note">
        {apiErrorMessage(error)} ·{" "}
        <a
          href="#"
          className="scope-retry"
          onClick={(e) => {
            e.preventDefault();
            void refetch();
          }}
        >
          ลองอีกครั้ง
        </a>
      </p>
    );

  const all = data?.projects ?? [];
  const terms = data?.terms ?? [];
  const needle = q.trim().toLowerCase();

  // ปีที่มีงาน ใหม่ก่อน — server เรียงมาแล้ว แต่หน้านี้ต้องรู้ว่ามีปีอะไรบ้างเพื่อทำปุ่มโหลดเพิ่ม
  const years = [...new Set(all.map((p) => p.year).filter(Boolean))];
  const openYears = years.slice(0, yearsOpen);
  const nextYear = years[yearsOpen];

  /**
   * พิมพ์กรอง = ค้นทุกปี · ไม่ได้พิมพ์ = เห็นแค่ปีที่กดเปิดไว้
   *
   * งานย้อนหลังมีถึงปี 2020 การโหลดมาทั้งหมดทำให้ต้องเลื่อนผ่านงานที่จบไปแล้วหลายสิบ
   * รายการก่อนจะถึงงานปีนี้ · แต่ถ้าพิมพ์ค้นแล้วยังจำกัดปีอยู่ จะกลายเป็น "ค้นไม่เจอ"
   * ทั้งที่งานนั้นมีอยู่ ซึ่งแย่กว่ารายการยาว
   */
  const shown = needle
    ? all.filter((p) => `${p.code} ${p.brand} ${p.name}`.toLowerCase().includes(needle))
    : all.filter((p) => openYears.includes(p.year));

  return (
    <div className="pf">
      <div className="pf-head">
        <div className="search-box grow-box">
          <IconSearch />
          <input
            type="text"
            placeholder="กรองด้วยรหัสหรือชื่องาน…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="muted small-note">
          {shown.length}/{all.length} งาน
          {!needle && openYears.length === 1 && ` · ปี ${openYears[0]}`}
        </span>
      </div>

      {!all.length ? (
        <p className="muted small-note pf-note">
          ไม่พบงานของแบรนด์นี้ในทะเบียน — ค้นด้วยคำว่า{" "}
          {terms.map((t) => `“${t}”`).join(", ") || "—"} · ถ้าชื่อในทะเบียนเขียนต่างจากนี้
          ให้แก้ชื่อกลุ่มหรือสร้างแคมเปญเองด้านบน
        </p>
      ) : (
        <div className="pf-list">
          {shown.map((p) => {
            const already = taken.has(p.campaignName.trim().toLowerCase());
            return (
              <div className="pf-row" key={`${p.code}:${p.name}`}>
                <span className="grow">
                  <span className="pf-name">{p.name}</span>
                  <span className="pf-meta">
                    <span className="code-chip">{p.code || "ไม่มีรหัส"}</span>
                    <span className="muted">{p.brand}</span>
                    {p.status && (
                      <span className={cn("pf-status", statusTone(p.status))}>{p.status}</span>
                    )}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  disabled={busy || already}
                  title={already ? "มีแคมเปญชื่อนี้แล้ว" : `สร้างแคมเปญ “${p.campaignName}”`}
                  onClick={() => onCreate(p.campaignName)}
                >
                  {already ? "สร้างแล้ว" : "+ สร้างแคมเปญ"}
                </button>
              </div>
            );
          })}
          {/* ปุ่มโหลดปีก่อนหน้าอยู่ท้ายรายการ — เลื่อนลงไปสุดแล้วจึงเจอ ไม่แย่งที่กับงานปีนี้ */}
          {!needle && nextYear && (
            <button type="button" className="pf-more" onClick={() => setYearsOpen((n) => n + 1)}>
              กดเพื่อโหลดปีก่อนหน้านี้ ({nextYear})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** สีของป้ายสถานะ — เลขนำหน้าใน Sheet บอกลำดับของงาน (0 ยกเลิก … 6 จบแล้ว) */
function statusTone(status: string): string {
  const n = status.trim()[0];
  if (n === "0") return "is-dead";
  if (n === "2" || n === "3" || n === "4") return "is-live";
  return "";
}

/* ───────── ชั้นที่ 2: รายละเอียดของแคมเปญเดียว ───────── */

function CampaignDetail({
  campaign,
  brandId,
  onBack,
}: {
  campaign: Campaign;
  brandId: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: campaignKeys.byProduct(brandId) });

  /**
   * ผูกได้ทีละหลายรายการ — เลือกหลายแคมเปญของ Ad Account เดียวแล้วกดเพิ่มครั้งเดียว
   * ยิงเรียงทีละตัวไม่ใช่พร้อมกัน เพราะทั้งชุดใช้ unique index เดียวกัน (campaign+kind+ref)
   * ยิงพร้อมกันแล้วจะแย่งกันเขียนแถวเดียวกันเมื่อผู้ใช้เผลอเลือกซ้ำ
   */
  const add = useMutation({
    mutationFn: async (v: { kind: string; items: LinkInput[] }) => {
      for (const it of v.items) await addCampaignLink(campaign.id, { kind: v.kind, ...it });
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteCampaignLink(id),
    onSuccess: invalidate,
  });

  /**
   * ทะเบียน Project Code อ่านช่อง Master Plan ที่ว่างเป็นงานที่ยังส่งแผนไม่ครบ
   * งานคอนเทนต์รายเดือนไม่เคยมีแผนใบเดียวอยู่แล้ว — ติ๊กตรงนี้เพื่อให้ทะเบียนแยกออก
   */
  const monthly = useMutation({
    mutationFn: (on: boolean) => setCampaignMonthly(campaign.id, on),
    onSuccess: () => {
      void invalidate();
      /*
       * ทะเบียนในหน้า Setting → Project Code ตั้ง staleTime ไว้ไม่จำกัด (โหลดทีเดียวใช้ทั้งวัน)
       * ถ้าไม่ล้างตรงนี้ คนที่เพิ่งติ๊กแล้วเปิดทะเบียนดูจะยังเห็นช่องว่าง เหมือนติ๊กไม่ติด
       * — โหลดใหม่รอบนี้ไม่ได้ไปอ่านชีตซ้ำ แค่ขอก้อนที่ server แคชไว้แล้ว
       */
      void qc.invalidateQueries({ queryKey: projectCodeKeys.all });
    },
  });

  return (
    <div className="camp-detail">
      <button type="button" className="btn-outline btn-sm camp-back" onClick={onBack}>
        ← กลับไปรายการแคมเปญ
      </button>
      <h4 className="camp-detail-name">{campaign.name}</h4>

      <label className="camp-monthly">
        <input
          type="checkbox"
          checked={campaign.monthlyContent}
          disabled={monthly.isPending}
          onChange={(e) => monthly.mutate(e.target.checked)}
        />
        <span className="grow">
          <span className="camp-monthly-name">เป็น Monthly Content</span>
          <span className="mem-meta">
            งานคอนเทนต์รายเดือน — ไม่ต้องมี Master Plan · ทะเบียน Project Code
            จะขึ้นเครื่องหมายไว้ในคอลัมน์ Monthly
          </span>
        </span>
      </label>
      {monthly.isError && <p className="muted small-note">{apiErrorMessage(monthly.error)}</p>}

      {LINK_TYPES.map((t) => {
        const links = linksOf(campaign, t.kind);
        return (
          <section className={cn("link-group", !t.ready && "is-soon")} key={t.kind}>
            <div className="link-group-head">
              <span>
                {t.icon} {t.label}
              </span>
              <span className="code-chip">{links.length}</span>
              {!t.ready && <span className="soon-tag">เร็วๆ นี้</span>}
            </div>
            <div className="link-items">
              {links.length ? (
                links.map((l) => (
                  <div className="link-item-row" key={l.id}>
                    <span className="grow">
                      <span className="provider-name">
                        <LinkName kind={t.kind} link={l} />
                      </span>
                      <span className="provider-status muted">{l.refId}</span>
                    </span>
                    <button
                      type="button"
                      className="icon-btn link-del"
                      title="เอาออก"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(l.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))
              ) : (
                <p className="muted small-note">{t.empty}</p>
              )}
            </div>
            {t.ready &&
              (t.platform ? (
                <ProjectAdd
                  type={t}
                  linked={links.map((l) => l.refId)}
                  onAdd={(items) => add.mutate({ kind: t.kind, items })}
                  busy={add.isPending}
                />
              ) : (
                <KolAdd
                  type={t}
                  linked={links.map((l) => l.refId)}
                  onAdd={(v) => add.mutate({ kind: "kol", items: [v] })}
                  busy={add.isPending}
                />
              ))}
          </section>
        );
      })}
    </div>
  );
}

/**
 * ชื่อที่แสดงของลิงก์
 *
 * ลิงก์เก่าที่ย้ายมาจาก kol_ref ไม่มีชื่อติดมา — หาจากรายการที่โหลดไว้ก่อน
 * ยังไม่เจอ (รายการโหลดไม่ได้เพราะ KOL ไม่รับ token) ค่อยถามชื่อทีละรหัส
 * ซึ่ง KOL เปิดให้อ่านโดยไม่ต้อง login จึงได้ชื่อจริงเสมอ ไม่ต้องโชว์รหัสเปล่า ๆ
 */
function LinkName({ kind, link }: { kind: string; link: CampaignLink }) {
  const { data: kolOptions } = useKolCampaigns();
  const fromList =
    kind === "kol" ? (kolOptions ?? []).find((o) => o.key === link.refId) : undefined;
  const needLookup = kind === "kol" && !link.refName && !fromList;
  const { data: one } = useKolCampaign(link.refId, needLookup);

  if (link.refName) return <>{link.refName}</>;
  if (fromList) return <>{fromList.name}</>;
  if (one?.name) return <>{one.name}</>;
  return <>{link.refId}</>;
}

function KolAdd({
  type,
  linked,
  onAdd,
  busy,
}: {
  type: LinkType;
  linked: string[];
  onAdd: (v: { refId: string; refName: string | null; meta: null }) => void;
  busy: boolean;
}) {
  const { data: options, isPending, isError, error, refetch } = useKolCampaigns();
  // ยังไม่ login = query ถูกปิดไว้ จึงค้าง pending ตลอด ต้องเช็คเองไม่ให้ค้าง "กำลังโหลด"
  const hasToken = !!wazzupToken();
  const [value, setValue] = useState("");
  const [msg, setMsg] = useState("");
  const available = (options ?? []).filter((o) => !linked.includes(o.key));
  const canPick = hasToken && !isError && !!options?.length;

  const submit = () => {
    const refId = value.trim();
    if (!refId) return;
    const opt = available.find((o) => o.key === refId);
    onAdd({ refId, refName: opt?.name ?? null, meta: null });
    setValue("");
  };

  /**
   * โหมดกรอกรหัสเอง — แปลงรหัสเป็นชื่อก่อนผูกเสมอ
   * ผูกด้วยรหัสเปล่า ๆ แล้วพิมพ์ผิดจะได้ลิงก์ตายที่ไม่มีใครรู้จนกดเข้าไปดู
   */
  const addByKey = async () => {
    const refId = value.trim();
    if (!refId) return;
    setMsg("กำลังตรวจรหัส…");
    try {
      const found = await fetchKolCampaign(refId);
      onAdd({ refId: found.key || refId, refName: found.name || null, meta: null });
      setValue("");
      setMsg("");
    } catch (e) {
      setMsg(`ไม่พบแคมเปญรหัส ${refId} ใน KOL Report (${apiErrorMessage(e)})`);
    }
  };

  if (hasToken && isPending)
    return (
      <div className="link-add">
        <select disabled>
          <option>กำลังโหลดรายชื่อแคมเปญ KOL…</option>
        </select>
      </div>
    );

  return (
    <div className="link-add">
      {canPick ? (
        <SearchSelect
          className="link-pick"
          options={available.map((o) => ({ value: o.key, label: `${o.name} (${o.key})` }))}
          value={value}
          onChange={setValue}
          placeholder={`พิมพ์ชื่อหรือรหัส${type.label}…`}
        />
      ) : (
        <>
          <input
            type="text"
            className="link-manual"
            placeholder={type.manualPlaceholder ?? ""}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void addByKey()}
          />
          <p className="muted small-note link-note">
            เลือกจากรายชื่อไม่ได้:{" "}
            {!hasToken
              ? "ยังไม่ได้เข้าสู่ระบบ"
              : isError
                ? apiErrorMessage(error)
                : "KOL Report ยังไม่มีแคมเปญที่เปิดใช้งาน"}{" "}
            ·{" "}
            <a
              href="#"
              className="scope-retry"
              onClick={(e) => {
                e.preventDefault();
                void refetch();
              }}
            >
              ลองโหลดอีกครั้ง
            </a>
            {msg && <> · {msg}</>}
          </p>
        </>
      )}
      <button
        type="button"
        className="btn-primary btn-sm link-add-btn"
        disabled={busy}
        onClick={() => (canPick ? submit() : void addByKey())}
      >
        + {type.addLabel ?? "เพิ่ม"}
      </button>
    </div>
  );
}

/**
 * เลือก project ฝั่ง Biddable ที่จะผูกกับแคมเปญนี้
 *
 * ผูกได้หลาย project ต่อหนึ่งแคมเปญของเรา (งานหนึ่งอาจแตกเป็นหลายรหัส)
 * รายชื่อมาจาก /biddable/projects ซึ่งเป็นหน่วยเดียวกับที่หน้า Campaign Report จัดกลุ่ม
 * ดึงไม่ได้ (ยังไม่ login / ไม่มีสิทธิ์) ก็ยังกรอกรหัสเองได้ ไม่ถึงกับตั้งค่าไม่ได้เลย
 */
function ProjectAdd({
  type,
  linked,
  onAdd,
  busy,
}: {
  type: LinkType;
  linked: string[];
  onAdd: (items: LinkInput[]) => void;
  busy: boolean;
}) {
  const { data: projects, isPending, isError, error, refetch } = useBiddableProjects();
  const hasToken = !!wazzupToken();
  const [value, setValue] = useState("");
  const [manual, setManual] = useState("");

  const available = (projects ?? []).filter((p) => !linked.includes(p.code));
  const canPick = hasToken && !isError && !!projects?.length;

  const submit = () => {
    if (canPick) {
      const code = value.trim();
      if (!code) return;
      const p = available.find((x) => x.code === code);
      onAdd([
        {
          refId: code,
          refName: p ? `${code} · ${p.name}` : code,
          // เก็บ platform ที่งานนี้มีไว้ด้วย ให้หน้า Online Ads บอกได้ว่าครอบคลุมอะไรบ้าง
          meta: { project: code, platforms: p?.platforms ?? [] },
        },
      ]);
      setValue("");
      return;
    }
    const code = manual.trim().toUpperCase();
    if (!code) return;
    onAdd([{ refId: code, refName: code, meta: { project: code, platforms: [] } }]);
    setManual("");
  };

  const addButton = (
    <button
      type="button"
      className="btn-primary btn-sm link-add-btn"
      disabled={busy}
      onClick={submit}
    >
      + {type.addLabel ?? "เพิ่ม"}
    </button>
  );

  if (hasToken && isPending)
    return (
      <div className="link-add">
        <select disabled>
          <option>กำลังโหลดรายชื่อ project…</option>
        </select>
        {addButton}
      </div>
    );

  if (!canPick)
    return (
      <div className="link-add">
        <input
          type="text"
          className="link-manual"
          placeholder={type.manualPlaceholder ?? ""}
          value={manual}
          onChange={(e) => setManual(e.target.value)}
        />
        <p className="muted small-note link-note">
          เลือกจากรายชื่อไม่ได้:{" "}
          {!hasToken ? "ยังไม่ได้เข้าสู่ระบบ" : isError ? apiErrorMessage(error) : "ไม่ทราบสาเหตุ"}{" "}
          ·{" "}
          <a
            href="#"
            className="scope-retry"
            onClick={(e) => {
              e.preventDefault();
              void refetch();
            }}
          >
            ลองโหลดอีกครั้ง
          </a>
        </p>
        {addButton}
      </div>
    );

  return (
    <div className="link-add">
      <SearchSelect
        className="link-pick"
        options={available.map((p) => ({
          value: p.code,
          label: `${p.code} · ${p.name} (${p.platforms.join(", ") || "—"}) · ${fmtBaht(p.spend)}`,
        }))}
        value={value}
        onChange={setValue}
        placeholder="พิมพ์ชื่อหรือรหัส project…"
      />
      {addButton}
    </div>
  );
}
